// src/components/RegionPageLayout.tsx
"use client";

import classNames from "classnames";
import Image from "next/image";
import React from "react";

import Breadcrumbs from "@/components/Breadcrumbs";
import CountryPageClient from "@/components/CountryPageClient";
import FAQSection from "@/components/FAQSection";
import Footer from "@/components/Footer";
import Header from "@/components/Header";
import SellingPoints from "@/components/SellingPoints";
import ClientWrapper from "@/components/ClientWrapper";
import { useTranslations } from "@/context/TranslationsContext";
import { getCurrencySymbol, isStripeCurrency } from "@/utils/currencyUtils";
import logger from "@/utils/logger.client";
import type { Language } from "@/translations/supportedLanguages";
import type { BreadcrumbItem, BundleWithCountries } from "@/types/types";

interface RegionPageLayoutProps {
  selectedLang: Language;
  processedCountry: {
    id: number;
    iso: string;
    name: string;
    bundles: BundleWithCountries[];
    networkBrands: string[];
  };
  sortedBundles: BundleWithCountries[];
  allCountries: { iso: string; name: string }[];
  breadcrumbItems: BreadcrumbItem[];
  backgroundImageUrl: string;
  productStructuredData: { image: string; [key: string]: any };
  messages: Record<string, string>;
  currency: string;
  exchangeRate: number;
}

export default function RegionPageLayout({
  selectedLang,
  processedCountry,
  sortedBundles,
  allCountries,
  breadcrumbItems,
  backgroundImageUrl,
  productStructuredData,
  messages,
  currency,
  exchangeRate,
}: RegionPageLayoutProps) {
  // Graceful Props Validation:
  if (!isStripeCurrency(currency) || exchangeRate <= 0) {
    logger.error("RegionPageLayout: Received invalid currency data", {
      currency,
      exchangeRate,
      region: processedCountry?.iso,
    });
    return (
      <div className="flex flex-col min-h-screen justify-center items-center bg-gray-100 text-red-600 text-center">
        <Header />
        <div className="p-8">
          <h2 className="text-2xl font-bold mb-2">Invalid region data</h2>
          <p>
            There was a problem displaying this region's data.
            <br />
            (Invalid currency or exchange rate)
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  const currencySymbol = getCurrencySymbol(currency) || "$";
  const countryIso = processedCountry.iso.toUpperCase();

  return (
    <ClientWrapper
      messages={messages}
      initialLanguage={selectedLang}
      initialCurrencyCode={currency}
      initialCurrencySymbol={currencySymbol}
      initialExchangeRate={exchangeRate}
      initialCountryIso={countryIso}
    >
      <LayoutInner
        selectedLang={selectedLang}
        processedCountry={processedCountry}
        sortedBundles={sortedBundles}
        allCountries={allCountries}
        breadcrumbItems={breadcrumbItems}
        backgroundImageUrl={backgroundImageUrl}
        productStructuredData={productStructuredData}
        currency={currency}
        exchangeRate={exchangeRate}
      />
    </ClientWrapper>
  );
}

const LayoutInner: React.FC<{
  selectedLang: Language;
  processedCountry: RegionPageLayoutProps["processedCountry"];
  sortedBundles: BundleWithCountries[];
  allCountries: { iso: string; name: string }[];
  breadcrumbItems: BreadcrumbItem[];
  backgroundImageUrl: string;
  productStructuredData: { image: string; [key: string]: any };
  currency: string;
  exchangeRate: number;
}> = ({
  selectedLang,
  processedCountry,
  sortedBundles,
  allCountries,
  breadcrumbItems,
  backgroundImageUrl,
  productStructuredData,
  currency,
  exchangeRate,
}) => {
  const { t } = useTranslations();

  const containerClass = classNames("flex flex-col min-h-screen bg-gray-100", {
    "dir-rtl": selectedLang === "ar",
    "dir-ltr": selectedLang !== "ar",
  });

  return (
    <div className={containerClass}>
      <Header />
      <Breadcrumbs items={breadcrumbItems} />

      <main className="container mx-auto px-4 flex-grow">
        <div className="relative rounded-2xl overflow-hidden h-96 md:h-[32rem]">
          <Image
            src={backgroundImageUrl || productStructuredData.image}
            alt={`${processedCountry.name} Background`}
            fill
            priority
            loading="eager"
            sizes="(max-width: 768px) 100vw, 1200px"
            style={{ objectFit: "cover" }}
            className="rounded-2xl"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-70" />
          <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 text-white h-full">
            <h1 className="text-3xl md:text-4xl font-extrabold mb-6">
              {t("regionPage.heroTitle", {
                regionName: processedCountry.name,
              })}
            </h1>
            <SellingPoints />
          </div>
        </div>

        <CountryPageClient
          country={processedCountry}
          bundles={sortedBundles}
          allCountries={allCountries}
          currencyCode={currency}
          exchangeRate={exchangeRate}
        />

        <FAQSection />
      </main>

      <Footer />
    </div>
  );
};

export { LayoutInner as MemoLayoutInner };
